import { useEffect, useRef } from 'react';
import { Ecommerce, EventNames, Taxonomy } from './contracts';
import { trackPage } from './trackPage';
import { trackSegmentAndGAEvent } from './trackSegmentAndGAEvent';

/**
 * @deprecated Replaced by {@link usePageSegmentTracking}
 * A tracking hook for pageview event. The 'page' parameter is virtual url path
 * for tracking only, e.g. in '/valuation/15116812/' page only sends
 * '/valuation/' to datalayer.
 *
 * @param {string} page - the page url for tracking
 * @param {object} extraTaxonomy - custom taxonomy object for pageview event
 * @param {object} extraEcommerce - custom ecommerce object for pageview event
 */
export const usePageTracking = (
    page?: string,
    extraTaxonomy: Record<string, unknown> = {},
    extraEcommerce: Record<string, unknown> | null = null
) => {
    useEffect(() => {
        const controller = new AbortController();
        if (page) {
            trackPage(page, extraTaxonomy, extraEcommerce, controller.signal);
        }
        return () => {
            controller.abort();
        };

        // This should only be called on the first render so omit the
        // dependencies that would cause this to be called multiple times
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

export type PageSegmentTrackingValues = {
    page: string;
    taxonomy?: Taxonomy;
    ecommerce?: Ecommerce;
};

/**
 * A tracking hook for pageview segment event. The 'page' parameter is virtual
 * url path for tracking only, e.g. in '/valuation/15116812/' page only sends
 * '/valuation/' to datalayer. values is "memoized" within this function to
 * prevent unexpected calls being made if these values are not memoized at source.
 *
 * @param {PageSegmentTrackingValues | null} values
 * @param {string} values.page the page url for tracking
 * @param {Taxonomy} values.taxonomy custom taxonomy object for pageview event
 * @param {Ecommerce} values.ecommerce custom ecommerce object for pageview event
 *
 * The values param can be passed as null to prevent the page tracking call to
 * allow using this hook conditionally, or to wait for a certain condition to be
 * true. Page tracking will not be called until this is though so use this
 * sparingly and ensure that tracking events are not lost with long async
 * conditions.
 *
 * @example
 * const property = await somePropertyLookup();
 * usePageTracking(
 *    property
 *    ? { page: 'page-name', taxonomy: { ...otherTaxonomy, uprn: property.uprn } }
 *    : null
 * );
 */
export const usePageSegmentTracking = (
    values: PageSegmentTrackingValues | null
) => {
    const valuesRef = useRef<typeof values>(null);

    useEffect(() => {
        // If the tracking values are the same as what they were before, we can
        // safely ignore the change and assume it's an un memoized value for
        // options.
        if (JSON.stringify(values) === JSON.stringify(valuesRef.current)) {
            return;
        }

        valuesRef.current = values;

        if (values) {
            trackSegmentAndGAEvent(
                EventNames.VIEWED_PAGE,
                {},
                values.page,
                values.taxonomy,
                values.ecommerce
            );
        }
    }, [values]);
};
